import { useState } from 'react';
import { Download, Upload, CheckCircle, AlertCircle, FileJson, Loader2 } from 'lucide-react';
import { exportData, useImportData } from '../../api/export-import';
import type { ExportData } from '../../api/export-import';

export function ImportExportPanel() {
    const [exportStatus, setExportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [importStatus, setImportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [importResult, setImportResult] = useState<{ personsImported: number; relationshipsImported: number } | null>(null);
    const [errorMessage, setErrorMessage] = useState('');

    const importMutation = useImportData();

    const handleExport = async () => {
        try {
            setExportStatus('loading');
            setErrorMessage('');

            const data = await exportData();

            // Create downloadable file
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `giapha-export-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            setExportStatus('success');
            setTimeout(() => setExportStatus('idle'), 3000);
        } catch (err: any) {
            setExportStatus('error');
            setErrorMessage(err.message || 'Lỗi khi xuất dữ liệu');
        }
    };

    const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setImportStatus('loading');
        setErrorMessage('');
        setImportResult(null);

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const text = e.target?.result as string;
                const data: ExportData = JSON.parse(text);

                // Basic client-side validation
                if (!data.version || !Array.isArray(data.persons) || !data.relationships) {
                    throw new Error('File JSON không đúng định dạng gia phả');
                }

                const result = await importMutation.mutateAsync(data);
                setImportResult(result);
                setImportStatus('success');
            } catch (err: any) {
                setImportStatus('error');
                setErrorMessage(
                    err.response?.data?.message ||
                    err.message ||
                    'Lỗi khi nhập dữ liệu'
                );
            }
        };

        reader.onerror = () => {
            setImportStatus('error');
            setErrorMessage('Không thể đọc file');
        };

        reader.readAsText(file);

        // Reset file input
        event.target.value = '';
    };

    return (
        <div className="card">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <FileJson className="w-6 h-6 text-primary-600" />
                Xuất / Nhập dữ liệu
            </h2>

            <div className="grid grid-cols-2 gap-4">
                {/* Export */}
                <div className="p-4 border border-gray-200 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50">
                    <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <Download className="w-4 h-4 text-blue-600" />
                        Xuất dữ liệu
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                        Tải toàn bộ dữ liệu gia phả về dạng file JSON
                    </p>
                    <button
                        onClick={handleExport}
                        disabled={exportStatus === 'loading'}
                        className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                        {exportStatus === 'loading' ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Đang xuất...
                            </>
                        ) : exportStatus === 'success' ? (
                            <>
                                <CheckCircle className="w-4 h-4" />
                                Đã tải xuống!
                            </>
                        ) : (
                            <>
                                <Download className="w-4 h-4" />
                                Xuất JSON
                            </>
                        )}
                    </button>
                </div>

                {/* Import */}
                <div className="p-4 border border-gray-200 rounded-xl bg-gradient-to-br from-amber-50 to-orange-50">
                    <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <Upload className="w-4 h-4 text-amber-600" />
                        Nhập dữ liệu
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                        Nhập dữ liệu gia phả từ file JSON đã xuất trước đó
                    </p>
                    <label
                        className={`w-full px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 cursor-pointer transition-colors ${importStatus === 'loading'
                                ? 'bg-amber-200 text-amber-800 cursor-not-allowed'
                                : importStatus === 'success'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-amber-600 text-white hover:bg-amber-700'
                            }`}
                    >
                        {importStatus === 'loading' ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Đang nhập...
                            </>
                        ) : importStatus === 'success' ? (
                            <>
                                <CheckCircle className="w-4 h-4" />
                                Nhập thành công!
                            </>
                        ) : (
                            <>
                                <Upload className="w-4 h-4" />
                                Chọn file JSON
                            </>
                        )}
                        <input
                            type="file"
                            accept=".json"
                            onChange={handleImport}
                            disabled={importStatus === 'loading'}
                            className="hidden"
                        />
                    </label>
                </div>
            </div>

            {/* Status messages */}
            {importStatus === 'success' && importResult && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-green-800">
                        <p className="font-medium">Nhập dữ liệu thành công!</p>
                        <p className="mt-1">
                            Đã nhập {importResult.personsImported} người và {importResult.relationshipsImported} quan hệ.
                            Cây gia phả sẽ tự động cập nhật.
                        </p>
                    </div>
                </div>
            )}

            {(exportStatus === 'error' || importStatus === 'error') && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-red-800">
                        <p className="font-medium">Có lỗi xảy ra</p>
                        <p className="mt-1">{errorMessage}</p>
                    </div>
                </div>
            )}

            <p className="mt-4 text-xs text-gray-400">
                ⚠️ Nhập dữ liệu sẽ thay thế toàn bộ dữ liệu hiện có. Hãy xuất dữ liệu để sao lưu trước khi nhập.
            </p>
        </div>
    );
}
